import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { CHANGE_WAREHOUSE_STATUS, DEL_WAREHOUSE } from "apollo/mutuations";
import { GET_MY_WARE_HOUSES } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import ShowWarehouseLocation from "components/Warehouse/showWarehouseLocation";
import WarehouseAddModal from "components/Warehouse/warehouseAddModal";
import WarehouseEditModal from "components/Warehouse/warehouseEditModal";
import { useEffect, useState } from "react";
import { Trash } from "tabler-icons-react";

const Warehouses = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [openedLocation, setOpenedLocation] = useState(false);
  const [location, setLocation] = useState({});

  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const roles = JSON.parse(localStorage.getItem("roles")) || [];

  const { data, loading, refetch } = useQuery(GET_MY_WARE_HOUSES, {
    variables: {
      first: parseInt(size),
      search: searchValue,
      page: activePage,
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to Get data's",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data?.myWarehouses.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const handleGeoLocationClick = (lat, lng) => {
    setLocation({
      lat: lat,
      lng: lng,
    });
    setOpenedLocation(true);
  };

  const [delWarehouse] = useMutation(DEL_WAREHOUSE, {
    update(cache, { data: { deleteWarehouse } }) {
      cache.updateQuery(
        {
          query: GET_MY_WARE_HOUSES,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: "",
          },
        },
        (data) => {
          setActivePage(activePage - 1);
          refetch();
          if (data.myWarehouses.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              myWarehouses: {
                data: [
                  ...data.myWarehouses.data.filter(
                    (warehouse) => warehouse.id !== deleteWarehouse.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const deleteWarehouse = () => {
    delWarehouse({
      variables: { id: deleteID },
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Warehouse Deleted Successfully",
        });
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Delete Warehouse",
        });
      },
    });
  };

  const handleEditCategory = (id) => {
    setEditId(id);
    setOpenedEdit(true);
  };

  const [changeWarehouseStatus] = useMutation(CHANGE_WAREHOUSE_STATUS, {
    refetchQueries: [
      {
        query: GET_MY_WARE_HOUSES,
        variables: {
          first: 10,
          page: activePage,
        },
      },
    ],
    onCompleted(data) {
      const action =
        data.changeWarehouseStatus.status === "ACTIVE"
          ? "Activated"
          : "Deactivated";
      showNotification({
        color: "green",
        title: "Success",
        message: `Warehouse ${action} successfully`,
      });
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to change status",
      });
    },
  });

  const handleWarehouseStatusChange = (id, currentStatus) => {
    let status;
    if (currentStatus === "DEACTIVATED") {
      status = "ACTIVE";
    } else {
      status = "DEACTIVATED";
    }
    changeWarehouseStatus({
      variables: {
        id: id,
        status: status,
      },
    });
  };

  const headerData = [
    {
      label: "Business Name",
      key: "name",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.supplier?.business_name}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      sortable: true,
      searchable: true,
      width: "15%",

      render: (rowData) => {
        return <span>{rowData.name}</span>;
      },
    },
    {
      label: "Region",
      key: "region",
      sortable: false,
      width: "10%", // Set a larger width for the Actions column
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.region?.name}</span>;
      },
    },
    {
      label: "Stock count",
      key: "name",
      sortable: false,
      width: "10%", // Set a larger width for the Actions column
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.stocks?.length}</span>;
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: false,
      searchable: false,
      width: "10%", // Set a smaller width for the Status column
      render: (rowData) => {
        return (
          <span
            style={{
              fontWeight: "bold",
              color: rowData.status === "ACTIVE" ? "green" : "red",
            }}
          >
            {rowData.status === "ACTIVE" ? "Active" : "Not Active"}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "30%", // Set a larger width for the Actions column
      render: (rowData) => {
        const { lat, lng } = rowData._geo;

        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="Edit"
              onClick={() => handleEditCategory(rowData)}
            >
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={15} />
            </ActionButton>
            <ActionButton
              label={rowData.status === "ACTIVE" ? "Deactivate" : "Activate"}
              color={rowData.status === "ACTIVE" ? "deactivate" : "activate"}
              onClick={() =>
                handleWarehouseStatusChange(rowData.id, rowData.status)
              }
            >
              {rowData.status === "ACTIVE" ? (
                <PersonOffIcon size={15} />
              ) : (
                <HowToRegIcon size={15} />
              )}
            </ActionButton>
            <ActionButton
              label="Location"
              color="primary"
              onClick={() => handleGeoLocationClick(lat, lng)}
            >
              <LocationOnIcon size={15} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: "Add Warehouse",
    setAddModal: setOpened,
  };

  const theme = useMantineTheme();
  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        title="Editing warehouse"
        padding="xl"
        onClose={() => setOpenedEdit(false)}
        position="bottom"
        size="80%"
      >
        <WarehouseEditModal
          openedEdit={openedEdit}
          setOpenedEdit={setOpenedEdit}
          editId={editId}
        />
      </Drawer>

      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure do you want todelete this warehouse?</p>
        <Group position="right">
          <Button onClick={() => deleteWarehouse()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title="Adding a warehouse"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <WarehouseAddModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
          refetch={refetch}
        />
      </Drawer>

      <Drawer
        opened={openedLocation}
        onClose={() => setOpenedLocation(false)}
        title="Warehouse Location"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <ShowWarehouseLocation location={location} />
      </Drawer>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            paginatorInfo={data?.myWarehouses?.paginatorInfo}
            activePage={activePage}
            handleChange={handleChange}
            clearInput={clearInput}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            header={headerData}
            data={data?.myWarehouses.data || []}
            loading={loading}
            optionsData={optionsData}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default Warehouses;
